/**
 * May Anthony configurator engine.
 * Config-driven per docs/louie-configurator/config/schema.md: nothing product-specific
 * lives here. A product config (steps/views/optionGroups/markingModes/fit) fully
 * determines the wizard's shape. Adding a product is a JSON + assets change only.
 */
(() => {
  const ROOT_SELECTOR = '.ma-configurator';

  /**
   * Cart adapter — the only store-specific seam. The engine is platform-neutral and
   * talks to whichever adapter it was given; this one carries every Shopify concern
   * (variant model, money format, line-item `properties[...]`, the cart-form POST).
   * A WooCommerceCartAdapter with the same surface plugs in via root.dataset.platform
   * (variation lookup, WP media upload, cart-item meta) with no engine change.
   *
   * Surface the engine relies on:
   *   initialGender() / initialVariantId()   seed the starting variant state
   *   genders()                              option list for the Fit select
   *   resolveVariant(state) -> {id, priceText} | null
   *   nameUploadInput(input, property)        name a picker input for submission
   *   wire({onError, onSuccess})              one-time cart plumbing + event hooks
   *   submit(design)                          add-to-cart from a collected design
   */
  class ShopifyCartAdapter {
    constructor(engine, root) {
      this.engine = engine;
      this.root = root;
      this.variants = JSON.parse(root.dataset.variants || '[]');
      this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
      this.formEl = root.querySelector('form');
      this.previewInput = null;
      this.propertyInputsContainer = null;
    }

    initialGender() { return this.variants[0] ? this.variants[0].option1 : "Men's"; }
    initialVariantId() { return this.variants[0] ? this.variants[0].id : null; }
    genders() { return [...new Set(this.variants.map(v => v.option1))]; }

    resolveVariant(state) {
      const v = this.variants.find(v => v.option1 === state.gender);
      return v ? { id: v.id, priceText: this.formatMoney(v.price) } : null;
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      return this.moneyFormat.replace('{{amount}}', amount).replace('{{ amount }}', amount);
    }

    // Upload picker inputs double as the form's file fields, so they carry the exact
    // Customily property names the atelier reads. (WooCommerce leaves them unnamed and
    // reads the File off the input at submit instead.)
    nameUploadInput(input, property) { input.name = `properties[${property}]`; }

    /* One-time: create the hidden _Preview file input and the property-inputs
       container inside the product form, and wire Prestige's cart events. Success is
       signaled on document.documentElement (theme.js dispatches it there so the drawer
       and badge can listen too); the drawer opening is the feedback, we just reset the
       button. hooks = { onError(msg), onSuccess() }. */
    wire(hooks) {
      this.previewInput = document.createElement('input');
      this.previewInput.type = 'file';
      this.previewInput.hidden = true;
      this.previewInput.name = 'properties[_Preview]';
      this.formEl.appendChild(this.previewInput);

      this.propertyInputsContainer = document.createElement('div');
      this.propertyInputsContainer.hidden = true;
      this.formEl.appendChild(this.propertyInputsContainer);

      this.formEl.addEventListener('cart:error', e => {
        hooks.onError(e.detail && e.detail.error ? e.detail.error : 'Something went wrong adding this to your cart. Please try again.');
      });
      document.documentElement.addEventListener('cart:change', e => {
        if (this.engine.submitting && e.detail && e.detail.baseEvent === 'variant:add') hooks.onSuccess();
      });
    }

    // Rebuild the hidden properties[...] inputs from the neutral [{key,value}] list.
    writeProperties(list) {
      this.propertyInputsContainer.textContent = '';
      list.forEach(({ key, value }) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${key}]`;
        input.value = value;
        this.propertyInputsContainer.appendChild(input);
      });
    }

    /* Attach the collected design to the product form and hand off to Prestige's
       <product-form>, which does the actual /cart/add.js post. design carries the
       neutral property list, the preview blob, the processed-artwork blobs (with their
       target inputs), and the upload/processed input maps to toggle for motif mode. */
    async submit(design) {
      // In motif mode the upload file inputs are empty; disable them so they don't
      // submit and clobber the motif URL properties. Re-enable otherwise.
      Object.values(design.uploadInputs).forEach(inp => { inp.disabled = design.motifMode; });
      Object.values(design.processedInputs).forEach(inp => { inp.disabled = design.motifMode; });

      this.writeProperties(design.properties);

      if (design.previewBlob) {
        this.engine.setFileInputFile(this.previewInput, new File([design.previewBlob], 'preview.png', { type: 'image/png' }));
      }
      design.processedItems.forEach(({ input, blob, name }) => {
        if (blob) this.engine.setFileInputFile(input, new File([blob], name, { type: 'image/png' }));
      });

      this.formEl.requestSubmit();
    }
  }

  class MaConfigurator {
    constructor(root) {
      this.root = root;
      this.configUrl = root.dataset.configUrl;
      this.assetBase = root.dataset.assetBase;
      this.productHandle = root.dataset.productHandle;
      // The only store-specific seam. Selected by platform; WooCommerce supplies
      // window.MaWooCommerceCartAdapter (loaded before this file by the WP plugin).
      const AdapterCtor = (root.dataset.platform === 'woocommerce' && window.MaWooCommerceCartAdapter)
        ? window.MaWooCommerceCartAdapter : ShopifyCartAdapter;
      this.adapter = new AdapterCtor(this, root);

      this.canvas = root.querySelector('[data-ma-canvas]');
      this.cx = this.canvas.getContext('2d');
      this.rail = root.querySelector('[data-ma-rail]');
      this.stepCountEl = root.querySelector('[data-ma-step-count]');
      this.stepsContainer = root.querySelector('[data-ma-steps]');
      this.toastEl = root.querySelector('[data-ma-toast]');

      this.imgCache = {};
      this.inkCache = {};
      this.renderQueued = false;
      this.fontsReady = null;

      this.emblemProcessedInputs = {};
      this.emblemUploadInputs = {};
      this.motifRenderCache = {};
      this.pickerTarget = null;
      this.submitting = false;
      this.toastTimer = null;

      this.state = {
        step: 0,
        visited: [true],
        gender: this.adapter.initialGender(),
        variantId: this.adapter.initialVariantId(),
      };
    }

    async init() {
      const res = await fetch(this.configUrl);
      this.config = await res.json();

      this.viewsById = Object.fromEntries(this.config.views.map(v => [v.id, v]));
      this.view = this.config.views.find(v => v.default) || this.config.views[0];
      this.canvas.width = this.view.frame.width;
      this.canvas.height = this.view.frame.height;

      this.optionGroups = this.config.optionGroups || [];
      this.markingModes = this.config.markingModes || [];
      this.stepDefs = this.config.steps;
      this.state.visited = this.stepDefs.map((_, i) => i === 0);

      this.setDefaultState();
      this.restoreState();
      this.loadFonts();
      this.buildRail();
      this.buildSteps();
      this.wireGlobalNav();
      this.wireTypingShrink();
      this.adapter.wire({
        onError: msg => { this.setSubmitting(false); this.say(msg); },
        onSuccess: () => this.setSubmitting(false),
      });
      this.reflectRestoredState();
      this.updateVariant();
      this.go(this.state.step || 0);
      this.render();
    }

    // ---------- state defaults ----------
    setDefaultState() {
      this.optionGroups.forEach(g => {
        if (g.values && g.values.length) this.state[g.id] = g.values[0].value;
      });
      this.state.design = null;
      this.markingModes.forEach(m => {
        (m.inputs || []).forEach(inp => { this.state[m.id + '_' + inp.id] = ''; });
        (m.uploads || []).forEach(u => { this.state[m.id + '_' + u.id] = null; });
        if (m.fonts && m.fonts.length) this.state[m.id + '_font'] = m.fonts[0].value;
        if (m.motifs) {
          this.state.emblemSource = m.motifs.defaultSource || 'upload';
          // Per-shoe motif selection, mirroring uploads (one design per shoe).
          (m.uploads || []).forEach(u => { this.state['emblemMotif_' + u.id] = null; });
        }
      });
      const fit = this.config.fit;
      this.state.region = fit.regions.values[0];
      this.state.size = fit.sizes.default[this.state.region];
      if (fit.width) this.state.width = fit.width.values[0].value;
    }

    /* ---------- persistence ----------
       A refresh used to wipe the whole design. Selections are kept in localStorage
       per product. Uploaded artwork cannot be stored (File/canvas objects, and a
       10MB image would blow the ~5MB quota), so only serialisable values are saved
       and an upload has to be re-chosen. */
    storageKey() { return `ma-cfg:${this.productHandle}`; }

    /* localStorage on this storefront is routinely full (third-party scripts store
       megabytes there), so every write can throw QuotaExceededError. Try it first
       for durability, fall back to sessionStorage, which has its own quota and at
       least survives a refresh. */
    stores() {
      const out = [];
      try { if (window.localStorage) out.push(window.localStorage); } catch (e) {}
      try { if (window.sessionStorage) out.push(window.sessionStorage); } catch (e) {}
      return out;
    }

    saveState() {
      const data = {};
      Object.keys(this.state).forEach(k => {
        const v = this.state[k];
        if (v === null || typeof v !== 'object') { data[k] = v; return; }
        // arrays of primitives (e.g. `visited`) are fine; skip canvases/Files
        if (Array.isArray(v) && v.every(x => x === null || typeof x !== 'object')) data[k] = v;
      });
      const payload = JSON.stringify({ v: 1, at: Date.now(), state: data });
      for (const s of this.stores()) {
        try { s.setItem(this.storageKey(), payload); return; } catch (e) { /* try the next store */ }
      }
    }

    restoreState() {
      let newest = null;
      for (const s of this.stores()) {
        try {
          const raw = s.getItem(this.storageKey());
          if (!raw) continue;
          const saved = JSON.parse(raw);
          if (!saved || saved.v !== 1 || !saved.state) continue;
          if (Date.now() - saved.at > 30 * 24 * 60 * 60 * 1000) { s.removeItem(this.storageKey()); continue; }
          // a store may hold a stale copy if a later write landed elsewhere
          if (!newest || saved.at > newest.at) newest = saved;
        } catch (e) { /* corrupt entry: ignore this store */ }
      }
      if (!newest) return;
      Object.assign(this.state, newest.state);
      this.restored = true;
    }

    persist() {
      clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => this.saveState(), 250);
    }

    /* Restored values that the builders don't pick up on their own. */
    reflectRestoredState() {
      if (!this.restored) return;
      const mode = this.markingModes.find(m => m.id === this.state.design);
      if (mode) {
        const radio = this.root.querySelector(`input[name="ma-design"][value="${mode.id}"]`);
        if (radio) radio.checked = true;
        (mode.inputs || []).forEach(inp => {
          const field = this.root.querySelector(`#ma-${mode.id}-${inp.id}`);
          if (field) field.value = this.state[mode.id + '_' + inp.id] || '';
        });
        (mode.uploads || []).forEach(u => {
          const id = this.state['emblemMotif_' + u.id];
          const item = id && mode.motifs && mode.motifs.items.find(x => x.id === id);
          if (!item) return;
          const thumb = this.root.querySelector(`[data-ma-motif-thumb="${u.id}"]`);
          if (thumb) {
            thumb.textContent = '';
            const img = document.createElement('img');
            img.src = this.assetUrl(item.asset);
            thumb.appendChild(img);
          }
          const cap = this.root.querySelector(`[data-ma-motif-value="${u.id}"]`);
          if (cap) cap.textContent = item.name;
          if (!this.motifRenderCache[item.id]) {
            this.layer(item.asset).then(img => { this.motifRenderCache[item.id] = img; this.render(); });
          }
        });
        this.syncDetailPanels();
      }
      /* An artwork order whose upload could not be restored must not resume past
         the marking step with nothing selected. */
      const markingIdx = this.stepDefs.findIndex(s => s.id === 'marking');
      if (this.state.step > markingIdx && !this.hasMarking()) this.state.step = markingIdx;
    }

    // ---------- assets ----------
    assetUrl(filename) { return this.assetBase + filename; }

    layer(filename) {
      if (!filename) return Promise.resolve(null);
      if (this.imgCache[filename]) return this.imgCache[filename];
      const p = new Promise(resolve => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = () => resolve(null);
        i.src = this.assetUrl(filename);
      });
      this.imgCache[filename] = p;
      return p;
    }

    loadFonts() {
      const faces = [];
      this.markingModes.forEach(m => (m.fonts || []).forEach(f => {
        faces.push(
          new FontFace(f.family, `url(${this.assetUrl(f.file)})`).load()
            .then(loaded => { document.fonts.add(loaded); return loaded; })
            .catch(() => null)
        );
      }));
      this.fontsReady = Promise.all(faces).then(() => this.render());
    }

    // ---------- rail ----------
    buildRail() {
      this.rail.innerHTML = '';
      this.stepDefs.forEach((step, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'ma-rail-step';
        b.appendChild(this.el('span', 'ma-dot', String(i + 1)));
        b.appendChild(this.el('span', 'ma-lbl', step.title));
        b.addEventListener('click', () => { if (this.state.visited[i]) this.go(i); });
        this.rail.appendChild(b);
      });
    }

    refreshRail() {
      [...this.rail.children].forEach((b, i) => {
        const done = i < this.state.step && this.stepComplete(i);
        b.classList.toggle('ma-current', i === this.state.step);
        b.classList.toggle('ma-done', done);
        b.disabled = !this.state.visited[i];
        if (i === this.state.step) b.setAttribute('aria-current', 'step');
        else b.removeAttribute('aria-current');
        const label = this.stepDefs[i].title;
        b.setAttribute('aria-label', `Step ${i + 1}: ${label}` + (done ? ', completed' : !this.state.visited[i] ? ', not yet reached' : ''));
      });
    }

    // ---------- steps: build DOM from config ----------
    buildSteps() {
      this.stepsContainer.innerHTML = '';
      this.stepDefs.forEach((step, i) => {
        const section = document.createElement('section');
        section.className = 'ma-step';
        section.dataset.step = String(i);
        section.setAttribute('aria-labelledby', `ma-s${i}h`);
        section.appendChild(this.buildStepBody(step, i));
        this.stepsContainer.appendChild(section);
      });
    }

    buildStepHeading(step, i, srOnly) {
      const h = document.createElement('h2');
      h.id = `ma-s${i}h`;
      if (srOnly) h.className = 'ma-sr-heading';
      h.textContent = step.heading || step.title;
      return h;
    }

    buildStepBody(step, i) {
      const frag = document.createDocumentFragment();
      if (step.id === 'upper') frag.appendChild(this.buildUpperStep(step, i));
      else if (step.id === 'marking') frag.appendChild(this.buildMarkingStep(step, i));
      else if (step.id === 'fit') frag.appendChild(this.buildFitStep(step, i));
      else if (step.id === 'review') frag.appendChild(this.buildReviewStep(step, i));
      return frag;
    }

    el(tag, className, text) {
      const e = document.createElement(tag);
      if (className) e.className = className;
      if (text != null) e.textContent = text;
      return e;
    }

    buildLede(step) {
      const p = this.el('p', 'ma-lede');
      p.textContent = step.lede;
      return p;
    }

    /* Price stacked above the shipping note, shown in the wizard on every step.
       One block per step, but only the active step is visible, so the customer
       never sees two prices. */
    buildPriceBlock() {
      const bar = this.el('div', 'ma-pricebar');
      const amount = this.el('span', 'ma-amount');
      amount.dataset.maPrice = 'true';
      bar.appendChild(amount);
      const note = (this.stepDefs[0] && this.stepDefs[0].footnote) || '';
      if (note) bar.appendChild(this.el('span', 'ma-terms', note));
      return bar;
    }

    buildStepNav({ back, nextLabel }) {
      const nav = this.el('div', 'ma-stepnav');
      if (back) {
        const b = this.el('button', 'ma-btn ma-btn-ghost', 'Back');
        b.type = 'button';
        b.addEventListener('click', () => this.go(this.state.step - 1));
        nav.appendChild(b);
      } else {
        nav.appendChild(this.el('span'));  // keeps the next button right-aligned
      }
      const n = this.el('button', 'ma-btn ma-btn-primary', nextLabel || 'Continue');
      n.type = 'button';
      n.dataset.maNext = 'true';
      nav.appendChild(n);
      return nav;
    }

    // ---- Step: Upper (structural option groups not tied to a marking mode) ----
    buildUpperStep(step, i) {
      const wrap = this.el('div', 'ma-step-body');
      wrap.appendChild(this.buildStepHeading(step, i, true));
      wrap.appendChild(this.buildLede(step));
      const groups = this.optionGroups.filter(g => !g.appliesTo);
      groups.forEach(g => wrap.appendChild(this.buildSwatchFieldset(g)));
      wrap.appendChild(this.buildPriceBlock());
      wrap.appendChild(this.buildStepNav({ nextLabel: 'Continue' }));
      return wrap;
    }

    buildSwatchFieldset(group) {
      const fs = this.el('fieldset');
      const legend = this.el('legend', null);
      /* Keep the label as ONE element: the legend is a space-between flex row, and a
         bare text node can be split by translation tools/extensions (Google Translate
         wraps words in <font>), which would scatter the label across the row. */
      legend.appendChild(this.el('span', 'ma-legend-text', group.label));
      fs.appendChild(legend);
      const container = this.el('div', 'ma-swatches');
      if (group.small) container.classList.add('ma-swatches--small');
      fs.appendChild(container);
      this.buildSwatches(container, group);
      return fs;
    }

    buildSwatches(container, group) {
      group.values.forEach(item => {
        const label = this.el('label', 'ma-swatch');
        if (group.small) label.classList.add('ma-swatch--small');
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = group.id;
        input.value = item.value;
        if (this.state[group.id] === item.value) input.checked = true;
        label.appendChild(input);
        const chip = this.el('span', 'ma-chip');
        const well = this.el('span', 'ma-well' + (group.small ? ' ma-small' : ''));
        const textureAsset = group.textured ? this.swatchTextureAsset(group, item) : null;
        if (textureAsset) {
          /* Crop window and filter come from renderRules.swatchStyle.crop — they were
             measured against the real photos so the dark colorways stay tellable apart. */
          const crop = ((this.config.renderRules || {}).swatchStyle || {}).crop || {};
          well.style.backgroundImage = `url(${this.assetUrl(textureAsset)})`;
          well.style.backgroundSize = crop.size || '500%';
          well.style.backgroundPosition = crop.position || '10% 70%';
          if (crop.filter) well.style.filter = crop.filter;
        } else {
          well.style.background = item.hex;
        }
        chip.appendChild(well);
        const name = this.el('span', 'ma-name', item.value);
        chip.appendChild(name);
        label.appendChild(chip);
        input.addEventListener('change', () => {
          this.state[group.id] = item.value;
          this.updateVariant();
          this.render();
        });
        container.appendChild(label);
      });
    }

    /* Which image to crop for a textured swatch tile. A group can point at another
       group's assets via `swatchTextureFrom` (e.g. Trim borrows Velvet's photo crops,
       because the trim's own layer PNGs are transparent overlays that would crop to
       an empty tile). Falls back to the group's own per-view asset. */
    swatchTextureAsset(group, item) {
      if (group.swatchTextureFrom) {
        const src = this.optionGroups.find(g => g.id === group.swatchTextureFrom);
        const match = src && src.values.find(v => v.value === item.value);
        if (match && match.assets) return match.assets[this.view.id];
      }
      return item.assets ? item.assets[this.view.id] : null;
    }

    // ---- Step: Marking (mode cards + reveal-in-place details) ----
    buildMarkingStep(step, i) {
      const wrap = this.el('div', 'ma-step-body');
      wrap.appendChild(this.buildStepHeading(step, i, false));
      wrap.appendChild(this.buildLede(step));

      const fs = this.el('fieldset');
      fs.appendChild(this.el('legend', null, 'Marking'));
      const cards = this.el('div', 'ma-cards');
      this.markingModes.forEach(mode => {
        const label = this.el('label', 'ma-card');
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = 'ma-design';
        input.value = mode.id;
        label.appendChild(input);
        const face = this.el('span', 'ma-face');
        const glyphClass = mode.fonts && mode.fonts[0] ? ' ' + mode.fonts[0].cssClass : '';
        face.appendChild(this.el('span', 'ma-glyph' + glyphClass, mode.glyph));
        face.appendChild(this.el('span', 'ma-t', mode.cardTitle));
        face.appendChild(this.el('span', 'ma-d', mode.cardDescription));
        label.appendChild(face);
        input.addEventListener('change', () => {
          this.state.design = mode.id;
          this.root.querySelector('[data-ma-v1]').textContent = '';
          this.syncDetailPanels();
          this.render();
        });
        cards.appendChild(label);
      });
      fs.appendChild(cards);
      wrap.appendChild(fs);

      const detailZone = this.el('div');
      detailZone.dataset.maDetailZone = 'true';
      detailZone.hidden = true;
      this.markingModes.forEach(mode => detailZone.appendChild(this.buildModeDetail(mode)));
      wrap.appendChild(detailZone);

      // thread (or any option group scoped to marking modes) shown alongside details
      this.optionGroups.filter(g => g.appliesTo).forEach(g => {
        const fieldset = this.buildSwatchFieldset(g);
        fieldset.dataset.maAppliesTo = g.appliesTo.join(',');
        const pickedSpan = this.el('span', 'ma-picked');
        pickedSpan.dataset.maPickedFor = g.id;
        pickedSpan.textContent = this.state[g.id];
        fieldset.querySelector('legend').appendChild(pickedSpan);
        detailZone.appendChild(fieldset);
      });

      const v1 = this.el('p', 'ma-validation');
      v1.dataset.maV1 = 'true';
      v1.setAttribute('role', 'alert');
      v1.setAttribute('aria-live', 'polite');
      wrap.appendChild(v1);
      wrap.appendChild(this.buildPriceBlock());
      wrap.appendChild(this.buildStepNav({ back: true, nextLabel: 'Continue' }));
      return wrap;
    }

    buildModeDetail(mode) {
      const box = this.el('div');
      box.dataset.maModeDetail = mode.id;
      box.hidden = true;

      if (mode.inputs) {
        const pair = this.el('div', 'ma-pair');
        mode.inputs.forEach(inp => {
          const field = this.el('div', 'ma-field');
          const label = this.el('label', null, inp.label);
          label.setAttribute('for', `ma-${mode.id}-${inp.id}`);
          field.appendChild(label);
          const input = document.createElement('input');
          input.type = 'text';
          input.id = `ma-${mode.id}-${inp.id}`;
          input.maxLength = inp.maxLength;
          input.autocomplete = 'off';
          input.spellcheck = false;
          if (inp.pattern === '[0-9]') input.inputMode = 'numeric';
          // Casing is config-driven via inp.transform (uppercase | lowercase | none).
          // This used to hardcode .toUpperCase(), so the shoe previewed in caps even
          // though the atelier embroiders lowercase - the preview contradicted the
          // product on every letters order.
          const cased = inp.transform === 'uppercase' ? (v => v.toUpperCase())
            : inp.transform === 'lowercase' ? (v => v.toLowerCase())
            : (v => v);
          const filter = inp.pattern === '[0-9]'
            ? v => v.replace(/[^0-9]/g, '')
            : v => cased(v.replace(/[^a-zA-Z]/g, ''));
          input.addEventListener('input', () => {
            input.value = filter(input.value);
            this.state[mode.id + '_' + inp.id] = input.value;
            const v1 = this.root.querySelector('[data-ma-v1]');
            if (v1) v1.textContent = '';
            this.render();
          });
          field.appendChild(input);
          pair.appendChild(field);
        });
        box.appendChild(pair);
        box.appendChild(this.el('p', 'ma-hintline', mode.hint));

        if (mode.fonts && mode.fonts.length > 1) {
          const fs = this.el('fieldset');
          fs.style.marginTop = '18px';
          fs.appendChild(this.el('legend', null, 'Lettering'));
          const cards = this.el('div', 'ma-cards');
          mode.fonts.forEach(f => {
            const label = this.el('label', 'ma-card');
            const input = document.createElement('input');
            input.type = 'radio';
            input.name = mode.id + '-font';
            input.value = f.value;
            if (this.state[mode.id + '_font'] === f.value) input.checked = true;
            label.appendChild(input);
            const face = this.el('span', 'ma-face');
            face.appendChild(this.el('span', `ma-glyph ${f.cssClass}`, 'A B'));
            face.appendChild(this.el('span', 'ma-t', f.displayLabel || f.value.charAt(0) + f.value.slice(1).toLowerCase()));
            label.appendChild(face);
            input.addEventListener('change', () => { this.state[mode.id + '_font'] = f.value; this.render(); });
            cards.appendChild(label);
          });
          fs.appendChild(cards);
          box.appendChild(fs);
        }
      } else if (mode.uploads || mode.motifs) {
        // Source toggle (Our designs / Upload your own) — only when both sources exist.
        if (mode.motifs && mode.uploads) box.appendChild(this.buildEmblemSourceToggle(mode));

        // Motif panel (per-shoe picker gallery)
        if (mode.motifs) {
          const motifPanel = this.el('div', 'ma-motif-panel');
          motifPanel.dataset.maEmblemPanel = 'motif';
          motifPanel.appendChild(this.buildMotifPanel(mode));
          box.appendChild(motifPanel);
        }

        // Upload panel (the existing per-shoe upload UI)
        if (mode.uploads) {
          const uploadPanel = this.el('div', 'ma-upload-panel');
          uploadPanel.dataset.maEmblemPanel = 'upload';
          const pair = this.el('div', 'ma-pair');
          mode.uploads.forEach(u => {
            const uploader = this.el('div', 'ma-uploader');
            uploader.appendChild(this.el('span', 'ma-field-label', u.label));
            const row = this.el('div', 'ma-row');
            const thumb = this.el('span', 'ma-thumb');
            thumb.dataset.maThumbFor = mode.id + '_' + u.id;
            row.appendChild(thumb);
            const btn = this.el('button', null, 'Choose artwork');
            btn.type = 'button';
            btn.setAttribute('aria-label', `Choose ${u.label.toLowerCase()} artwork`);
            const fileInput = document.createElement('input');
            fileInput.type = 'file';
            fileInput.hidden = true;
            fileInput.accept = mode.upload.accept;
            this.adapter.nameUploadInput(fileInput, u.property);
            this.emblemUploadInputs[mode.id + '_' + u.id] = fileInput;
            btn.addEventListener('click', () => fileInput.click());
            fileInput.addEventListener('change', () => this.handleUpload(fileInput, mode, u, thumb));
            row.appendChild(btn);
            uploader.appendChild(row);
            pair.appendChild(uploader);
            uploadPanel.appendChild(fileInput);

            if (u.processedProperty) {
              const processedInput = document.createElement('input');
              processedInput.type = 'file';
              processedInput.hidden = true;
              this.adapter.nameUploadInput(processedInput, u.processedProperty);
              this.emblemProcessedInputs[mode.id + '_' + u.id] = processedInput;
              uploadPanel.appendChild(processedInput);
            }
          });
          uploadPanel.insertBefore(pair, uploadPanel.firstChild);
          if (mode.specs) uploadPanel.appendChild(this.el('p', 'ma-specs', mode.specs));
          box.appendChild(uploadPanel);
        }
      }
      return box;
    }

    buildEmblemSourceToggle(mode) {
      const labels = mode.motifs.sourceLabels || {};
      const order = mode.motifs.sourceOrder || ['upload', 'motif'];
      const wrap = this.el('div', 'ma-segmented');
      wrap.setAttribute('role', 'tablist');
      order.forEach(source => {
        const btn = this.el('button', 'ma-seg-btn', labels[source] || source);
        btn.type = 'button';
        btn.dataset.maEmblemSource = source;
        btn.setAttribute('role', 'tab');
        if (this.state.emblemSource === source) btn.classList.add('ma-seg-active');
        btn.addEventListener('click', () => this.setEmblemSource(source));
        wrap.appendChild(btn);
      });
      return wrap;
    }

    /* Per-shoe motif panel: a slot (thumbnail + "Choose design" button) for each
       shoe, mirroring the upload UI, plus one reusable inline picker (the gallery)
       that opens targeted at whichever shoe's button was clicked. */
    buildMotifPanel(mode) {
      const panel = this.el('div');
      const pickLabel = mode.motifs.pickLabel || 'Choose design';

      const pair = this.el('div', 'ma-pair');
      mode.uploads.forEach(u => {
        const slot = this.el('div', 'ma-uploader');
        slot.appendChild(this.el('span', 'ma-field-label', u.label));
        const row = this.el('div', 'ma-row');
        const thumb = this.el('span', 'ma-thumb');
        thumb.dataset.maMotifThumb = u.id;
        row.appendChild(thumb);
        const btn = this.el('button', null, pickLabel);
        btn.type = 'button';
        btn.dataset.maMotifPick = u.id;
        btn.setAttribute('aria-label', `${pickLabel} for ${u.label.toLowerCase()}`);
        btn.addEventListener('click', () => this.openMotifPicker(mode, u));
        row.appendChild(btn);
        slot.appendChild(row);
        pair.appendChild(slot);
      });
      panel.appendChild(pair);

      // Reusable picker (hidden until a "Choose design" button is clicked)
      const picker = this.el('div', 'ma-motif-picker');
      picker.dataset.maMotifPicker = 'true';
      picker.hidden = true;
      const heading = this.el('p', 'ma-picker-heading');
      heading.dataset.maPickerHeading = 'true';
      picker.appendChild(heading);
      const grid = this.el('div', 'ma-motif-grid');
      mode.motifs.items.forEach(item => {
        const btn = this.el('button', 'ma-motif');
        btn.type = 'button';
        btn.dataset.maMotif = item.id;
        btn.setAttribute('aria-label', item.name);
        const img = document.createElement('img');
        img.src = this.assetUrl(item.asset);
        img.alt = item.name;
        img.loading = 'lazy';
        btn.appendChild(img);
        btn.appendChild(this.el('span', 'ma-motif-name', item.name));
        btn.addEventListener('click', () => this.selectMotif(mode, item));
        grid.appendChild(btn);
      });
      picker.appendChild(grid);
      panel.appendChild(picker);

      return panel;
    }

    openMotifPicker(mode, uploadDef) {
      this.pickerTarget = uploadDef;
      const picker = this.root.querySelector('[data-ma-motif-picker]');
      const heading = this.root.querySelector('[data-ma-picker-heading]');
      heading.textContent = `Choose a design for the ${uploadDef.label.toLowerCase()}`;
      // Highlight this shoe's current selection in the gallery.
      const current = this.state['emblemMotif_' + uploadDef.id];
      picker.querySelectorAll('[data-ma-motif]').forEach(b =>
        b.classList.toggle('ma-motif-active', b.dataset.maMotif === current));
      picker.hidden = false;
    }

    async selectMotif(mode, item) {
      const u = this.pickerTarget;
      if (!u) return;
      this.state['emblemMotif_' + u.id] = item.id;
      // Update this shoe's thumbnail.
      const thumb = this.root.querySelector(`[data-ma-motif-thumb="${u.id}"]`);
      if (thumb) {
        thumb.textContent = '';
        const ti = document.createElement('img');
        ti.src = this.assetUrl(item.asset);
        thumb.appendChild(ti);
      }
      const v1 = this.root.querySelector('[data-ma-v1]');
      if (v1) v1.textContent = '';
      if (!this.motifRenderCache[item.id]) {
        this.motifRenderCache[item.id] = await this.layer(item.asset);
      }
      const picker = this.root.querySelector('[data-ma-motif-picker]');
      if (picker) picker.hidden = true;
      this.render();
    }

    setEmblemSource(source) {
      this.state.emblemSource = source;
      this.root.querySelectorAll('[data-ma-emblem-source]').forEach(b =>
        b.classList.toggle('ma-seg-active', b.dataset.maEmblemSource === source));
      this.root.querySelectorAll('[data-ma-emblem-panel]').forEach(p =>
        p.hidden = p.dataset.maEmblemPanel !== source);
      // Close any open picker when switching source.
      const picker = this.root.querySelector('[data-ma-motif-picker]');
      if (picker) picker.hidden = true;
      const v1 = this.root.querySelector('[data-ma-v1]');
      if (v1) v1.textContent = '';
      this.render();
    }

    syncDetailPanels() {
      const zone = this.root.querySelector('[data-ma-detail-zone]');
      zone.hidden = !this.state.design;
      this.markingModes.forEach(mode => {
        const panel = this.root.querySelector(`[data-ma-mode-detail="${mode.id}"]`);
        if (panel) panel.hidden = this.state.design !== mode.id;
      });
      this.root.querySelectorAll('[data-ma-applies-to]').forEach(fs => {
        const modes = fs.dataset.maAppliesTo.split(',');
        fs.style.display = modes.includes(this.state.design) ? '' : 'none';
      });
      // Emblem source panels (motif gallery vs upload) follow the current source.
      this.root.querySelectorAll('[data-ma-emblem-panel]').forEach(p =>
        p.hidden = p.dataset.maEmblemPanel !== this.state.emblemSource);
    }

    handleUpload(fileInput, mode, uploadDef, thumbEl) {
      const f = fileInput.files[0];
      if (!f) return;
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const processed = mode.upload.cropToArtwork ? this.processEmblem(img) : img;
          this.state[mode.id + '_' + uploadDef.id] = { canvas: processed, sourceFile: f };
          thumbEl.innerHTML = '';
          const ti = document.createElement('img');
          ti.src = processed.toDataURL ? processed.toDataURL() : img.src;
          thumbEl.appendChild(ti);
          const v1 = this.root.querySelector('[data-ma-v1]');
          if (v1) v1.textContent = '';
          this.render();
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(f);
    }

    /* Border flood-fill background removal + crop-to-artwork, matching Customily's
       processing so orders look consistent with the atelier's expectations. */
    processEmblem(img) {
      const MAX = 1000;
      const s = Math.min(1, MAX / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * s)), h = Math.max(1, Math.round(img.height * s));
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      const ctx = c.getContext('2d'); ctx.drawImage(img, 0, 0, w, h);
      const id = ctx.getImageData(0, 0, w, h); const d = id.data;
      const px = (x, y) => (y * w + x) * 4;
      const corners = [px(0, 0), px(w - 1, 0), px(0, h - 1), px(w - 1, h - 1)];
      const bg = [0, 1, 2].map(k => corners.map(i => d[i + k]).sort((a, b) => a - b)[1]);
      const TOL = 140;
      const isBg = i => d[i + 3] < 20 || (Math.abs(d[i] - bg[0]) + Math.abs(d[i + 1] - bg[1]) + Math.abs(d[i + 2] - bg[2])) < TOL;
      const visited = new Uint8Array(w * h); const stack = [];
      for (let x = 0; x < w; x++) { stack.push(x, (h - 1) * w + x); }
      for (let y = 0; y < h; y++) { stack.push(y * w, y * w + w - 1); }
      while (stack.length) {
        const p = stack.pop(); if (visited[p]) continue; visited[p] = 1;
        const i = p * 4; if (!isBg(i)) continue;
        d[i + 3] = 0;
        const x = p % w, y = (p - x) / w;
        if (x > 0) stack.push(p - 1); if (x < w - 1) stack.push(p + 1);
        if (y > 0) stack.push(p - w); if (y < h - 1) stack.push(p + w);
      }
      ctx.putImageData(id, 0, 0);
      let x0 = w, y0 = h, x1 = -1, y1 = -1;
      for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        if (d[px(x, y) + 3] > 20) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
      }
      if (x1 < 0) return c;
      const pad = Math.round(Math.max(x1 - x0, y1 - y0) * 0.02);
      x0 = Math.max(0, x0 - pad); y0 = Math.max(0, y0 - pad); x1 = Math.min(w - 1, x1 + pad); y1 = Math.min(h - 1, y1 + pad);
      const out = document.createElement('canvas'); out.width = x1 - x0 + 1; out.height = y1 - y0 + 1;
      out.getContext('2d').drawImage(c, x0, y0, out.width, out.height, 0, 0, out.width, out.height);
      return out;
    }

    // ---- Step: Fit ----
    buildFitStep(step, i) {
      const fit = this.config.fit;
      const wrap = this.el('div', 'ma-step-body');
      wrap.appendChild(this.buildStepHeading(step, i, false));
      wrap.appendChild(this.buildLede(step));

      const pair1 = this.el('div', 'ma-pair');
      // Gender/fit is optional: shown only when the product config declares a variantOption.
      // Shopify's Louie does; NG Louie is single-gender and omits it.
      if (this.config.fit.variantOption) {
        const genderField = this.el('div', 'ma-field');
        genderField.appendChild(this.el('label', null, 'Fit')).setAttribute('for', 'ma-gender');
        const genderSel = document.createElement('select');
        genderSel.id = 'ma-gender';
        this.adapter.genders().forEach(g => {
          const o = document.createElement('option'); o.value = g; o.textContent = g;
          if (g === this.state.gender) o.selected = true;
          genderSel.appendChild(o);
        });
        genderSel.addEventListener('change', e => { this.state.gender = e.target.value; this.updateVariant(); this.persist(); });
        genderField.appendChild(genderSel);
        pair1.appendChild(genderField);
      }

      // A single sizing standard needs no dropdown - the size list is fixed to it. Shown
      // only when the config offers more than one region (e.g. Shopify's US/EU/UK).
      let regionSel = null;
      if (fit.regions.values.length > 1) {
        const regionField = this.el('div', 'ma-field');
        regionField.appendChild(this.el('label', null, 'Sizing')).setAttribute('for', 'ma-region');
        regionSel = document.createElement('select');
        regionSel.id = 'ma-region';
        fit.regions.values.forEach(r => {
          const o = document.createElement('option'); o.value = r; o.textContent = r;
          if (r === this.state.region) o.selected = true;
          regionSel.appendChild(o);
        });
        regionField.appendChild(regionSel);
        pair1.appendChild(regionField);
      }
      wrap.appendChild(pair1);

      const pair2 = this.el('div', 'ma-pair');
      const sizeField = this.el('div', 'ma-field');
      sizeField.appendChild(this.el('label', null, 'Size')).setAttribute('for', 'ma-size');
      const sizeSel = document.createElement('select');
      sizeSel.id = 'ma-size';
      const fillSizes = () => {
        sizeSel.innerHTML = '';
        fit.sizes.values[this.state.region].forEach(s => {
          const o = document.createElement('option'); o.value = s; o.textContent = s;
          if (s === this.state.size) o.selected = true;
          sizeSel.appendChild(o);
        });
      };
      fillSizes();
      if (regionSel) {
        regionSel.addEventListener('change', e => {
          this.state.region = e.target.value;
          this.state.size = fit.sizes.default[this.state.region];
          this.persist();
          fillSizes();
        });
      }
      sizeSel.addEventListener('change', e => { this.state.size = e.target.value; this.persist(); });
      sizeField.appendChild(sizeSel);
      pair2.appendChild(sizeField);

      // Width is optional: shown only when the config declares fit.width. NG Louie is
      // size-only, so its config omits it.
      if (fit.width) {
        const widthField = this.el('div', 'ma-field');
        widthField.appendChild(this.el('label', null, 'Width')).setAttribute('for', 'ma-width');
        const widthSel = document.createElement('select');
        widthSel.id = 'ma-width';
        fit.width.values.forEach(w => {
          const o = document.createElement('option'); o.value = w.value; o.textContent = w.label;
          if (w.value === this.state.width) o.selected = true;
          widthSel.appendChild(o);
        });
        widthSel.addEventListener('change', e => { this.state.width = e.target.value; this.persist(); });
        widthField.appendChild(widthSel);
        pair2.appendChild(widthField);
      }
      wrap.appendChild(pair2);

      wrap.appendChild(this.el('p', 'ma-hintline', fit.hint));
      wrap.appendChild(this.buildPriceBlock());
      wrap.appendChild(this.buildStepNav({ back: true, nextLabel: 'Review' }));
      return wrap;
    }

    // ---- Step: Review ----
    buildReviewStep(step, i) {
      const wrap = this.el('div', 'ma-step-body');
      wrap.appendChild(this.buildStepHeading(step, i, false));
      wrap.appendChild(this.buildLede(step));

      const summary = this.el('div', 'ma-summary');
      summary.dataset.maSummary = 'true';
      wrap.appendChild(summary);

      const notes = this.el('div', 'ma-notes');
      notes.appendChild(this.el('p', null, step.disclaimer));
      wrap.appendChild(notes);

      const consentRow = this.el('label', 'ma-consent');
      consentRow.dataset.maConsentRow = 'true';
      consentRow.hidden = true;
      const consentBox = document.createElement('input');
      consentBox.type = 'checkbox';
      consentBox.dataset.maConsentBox = 'true';
      consentRow.appendChild(consentBox);
      consentRow.appendChild(this.el('span', null, step.consent.text));
      consentBox.addEventListener('change', () => { this.root.querySelector('[data-ma-v5]').textContent = ''; });
      wrap.appendChild(consentRow);

      const v5 = this.el('p', 'ma-validation');
      v5.dataset.maV5 = 'true';
      v5.setAttribute('role', 'alert');
      v5.setAttribute('aria-live', 'polite');
      wrap.appendChild(v5);

      wrap.appendChild(this.buildPriceBlock());

      const nav = this.el('div', 'ma-stepnav');
      const back = this.el('button', 'ma-btn ma-btn-ghost', 'Back');
      back.type = 'button';
      back.addEventListener('click', () => this.go(this.state.step - 1));
      nav.appendChild(back);
      const atc = this.el('button', 'ma-btn ma-btn-primary', 'Add to cart');
      atc.type = 'button';
      atc.dataset.maAddToCart = 'true';
      atc.addEventListener('click', () => this.prepareAndSubmit(atc));
      nav.appendChild(atc);
      wrap.appendChild(nav);

      return wrap;
    }

    buildSummary() {
      const labels = this.stepDefs.find(s => s.id === 'review').summaryLabels;
      const [markingMain, markingNote] = this.markingSummaryParts();
      const rows = [
        [labels.velvet, this.state.velvet, null, 0, labels.velvet.toLowerCase()],
        [labels.trim, this.state.trim, null, 0, labels.trim.toLowerCase()],
        [labels.marking, markingMain, markingNote, 1, labels.marking.toLowerCase()],
        [labels.fit, (this.config.fit.variantOption ? this.state.gender + ' · ' : '') + this.state.size + (this.config.fit.width ? ` · ${this.state.width} width` : ''), null, 2, labels.fit.toLowerCase()],
      ];
      const summary = this.root.querySelector('[data-ma-summary]');
      summary.textContent = '';
      rows.forEach(([k, main, note, stepIdx, aria]) => {
        const row = this.el('div', 'ma-row');
        row.appendChild(this.el('span', 'ma-k', k));
        const v = this.el('span', 'ma-v');
        v.appendChild(document.createTextNode(main));
        if (note) {
          v.appendChild(document.createTextNode(' '));
          v.appendChild(this.el('em', null, note));
        }
        const editBtn = this.el('button', 'ma-edit', 'Change');
        editBtn.type = 'button';
        editBtn.dataset.maGoto = String(stepIdx);
        editBtn.setAttribute('aria-label', `Change ${aria}`);
        editBtn.addEventListener('click', () => this.go(stepIdx));
        v.appendChild(editBtn);
        row.appendChild(v);
        summary.appendChild(row);
      });
      const consentRow = this.root.querySelector('[data-ma-consent-row]');
      const consentDef = this.stepDefs.find(s => s.id === 'review').consent;
      // Rights confirmation applies to customer-uploaded artwork only, not house motifs.
      const isUploadArtwork = this.state.design === consentDef.appliesTo && this.state.emblemSource !== 'motif';
      consentRow.hidden = !isUploadArtwork;
      this.root.querySelector('[data-ma-v5]').textContent = '';
    }

    /* Returns [mainText, noteText|null] — kept as plain text pairs (not HTML) since
       mainText carries customer-typed characters straight into the DOM. */
    markingSummaryParts() {
      const mode = this.markingModes.find(m => m.id === this.state.design);
      if (!mode) return ['Not chosen', null];
      if (mode.inputs) {
        const [right, left] = mode.inputs;
        const rightVal = this.state[mode.id + '_' + right.id] || 'left plain';
        const leftVal = this.state[mode.id + '_' + left.id] || 'left plain';
        const threadGroup = this.optionGroups.find(g => g.appliesTo && g.appliesTo.includes(mode.id));
        const threadName = threadGroup ? this.state[threadGroup.id] : null;
        const main = `${mode.label}: ${rightVal} / ${leftVal}`;
        if (mode.fonts && mode.fonts.length > 1) {
          const font = mode.fonts.find(f => f.value === this.state[mode.id + '_font']);
          const fontLabel = (font.displayLabel || font.value).toLowerCase();
          const note = `(${fontLabel}${threadName ? ', ' + threadName.toLowerCase() + ' thread' : ''})`;
          return [main, note];
        }
        return [main, threadName ? `(${threadName.toLowerCase()} thread)` : null];
      }
      if (mode.motifs && this.state.emblemSource === 'motif') {
        const nameFor = id => { const m = mode.motifs.items.find(x => x.id === id); return m ? m.name : 'left plain'; };
        const [right, left] = mode.uploads;
        return [`${nameFor(this.state['emblemMotif_' + right.id])} / ${nameFor(this.state['emblemMotif_' + left.id])}`, null];
      }
      return [`Your ${mode.label}`, null];
    }

    // ---------- nav / validation ----------
    stepComplete(i) {
      const step = this.stepDefs[i];
      if (step.id === 'upper') return this.optionGroups.filter(g => !g.appliesTo).every(g => !!this.state[g.id]);
      if (step.id === 'marking') return !!this.state.design && this.hasMarking();
      if (step.id === 'fit') return !!this.state.size;
      return true;
    }

    hasMarking() {
      const mode = this.markingModes.find(m => m.id === this.state.design);
      if (!mode) return false;
      if (mode.inputs) return mode.inputs.some(inp => this.state[mode.id + '_' + inp.id]);
      if (mode.motifs && this.state.emblemSource === 'motif') return mode.uploads.some(u => this.state['emblemMotif_' + u.id]);
      if (mode.uploads) return mode.uploads.some(u => this.state[mode.id + '_' + u.id]);
      return false;
    }

    validate(i) {
      const step = this.stepDefs[i];
      if (step.id === 'marking') {
        if (!this.state.design) return 'Choose how we should mark your pair to continue.';
        const mode = this.markingModes.find(m => m.id === this.state.design);
        if (mode.inputs && !this.hasMarking()) return `Add at least one ${mode.label.toLowerCase().replace(/s$/, '')}. Either shoe will do.`;
        if (mode.motifs && this.state.emblemSource === 'motif' && !this.hasMarking()) return 'Choose a design for at least one shoe.';
        if (mode.uploads && this.state.emblemSource !== 'motif' && !this.hasMarking()) return 'Choose artwork for at least one shoe.';
      }
      return '';
    }

    wireGlobalNav() {
      this.stepsContainer.addEventListener('click', e => {
        if (e.target.dataset.maNext !== undefined && e.target.matches('[data-ma-next]')) {
          const msg = this.validate(this.state.step);
          const box = this.root.querySelector('.ma-step.ma-active [data-ma-v1]');
          if (msg) { if (box) box.textContent = msg; return; }
          this.go(this.state.step + 1);
        }
      });
    }

    go(i) {
      this.state.step = i;
      this.state.visited[i] = true;
      this.stepsContainer.querySelectorAll('.ma-step').forEach(s => s.classList.toggle('ma-active', +s.dataset.step === i));
      this.stepsContainer.querySelectorAll('.ma-validation').forEach(v => v.textContent = '');
      if (this.stepDefs[i].id === 'marking' && this.state.design) this.syncDetailPanels();
      if (this.stepDefs[i].id === 'review') this.buildSummary();
      this.refreshRail();
      this.stepCountEl.textContent = `Step ${i + 1} of ${this.stepDefs.length} · ${this.stepDefs[i].title}`;
      /* Don't yank the page on every step change, but do rescue the step when it is
         out of sight. On mobile the preview is pinned, so "out of sight" means hidden
         behind it, not just above the viewport: land the content below the pinned
         preview rather than underneath it. */
      const stageCol = this.root.querySelector('.ma-stage-col');
      const pinned = stageCol && getComputedStyle(stageCol).position === 'sticky';
      const cover = pinned ? stageCol.getBoundingClientRect().bottom : 0;
      const stepTop = this.stepsContainer.getBoundingClientRect().top;
      if (stepTop < cover - 4) {
        window.scrollTo({ top: window.scrollY + stepTop - cover - 12, behavior: 'smooth' });
      }
      const heading = this.stepsContainer.querySelector('.ma-step.ma-active h2');
      if (heading) { heading.setAttribute('tabindex', '-1'); heading.focus({ preventScroll: true }); }
    }

    // ---------- variant / price ----------
    updateVariant() {
      const resolved = this.adapter.resolveVariant(this.state);
      if (!resolved) return;
      this.state.variantId = resolved.id;
      const idInput = this.root.querySelector('[data-ma-variant-id]');
      if (idInput) idInput.value = resolved.id;
      // Every price on the page: the one beside the preview and the Review total.
      this.root.querySelectorAll('[data-ma-price]').forEach(el => { el.textContent = resolved.priceText; });
    }

    /* On mobile the preview is pinned to the top, so an open keyboard would squeeze
       the fields. Flag while a text field is focused; the CSS shrinks the preview
       (mobile only) so both the shoe and the field being edited stay visible. */
    wireTypingShrink() {
      const isText = el => el && el.tagName === 'INPUT' && el.type === 'text';
      this.root.addEventListener('focusin', e => {
        if (isText(e.target)) this.root.classList.add('ma-typing');
      });
      this.root.addEventListener('focusout', e => {
        if (isText(e.target)) this.root.classList.remove('ma-typing');
      });
    }

    // ---------- cart submit ----------
    setSubmitting(isSubmitting) {
      this.submitting = isSubmitting;
      const atc = this.root.querySelector('[data-ma-add-to-cart]');
      if (!atc) return;
      atc.disabled = isSubmitting;
      atc.setAttribute('aria-busy', String(isSubmitting));
      atc.textContent = isSubmitting ? 'Adding…' : 'Add to cart';
    }

    say(msg) {
      if (!this.toastEl) return;
      this.toastEl.textContent = msg;
      this.toastEl.classList.add('ma-show');
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => this.toastEl.classList.remove('ma-show'), 4200);
    }

    toBlobAsync(canvas) {
      return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
    }

    setFileInputFile(input, file) {
      const dt = new DataTransfer();
      dt.items.add(file);
      input.files = dt.files;
    }

    /* Collects every visible + hidden text property from current state as a neutral
       [{key,value}] list. The key strings come from config (Customily's byte-for-byte
       names on Shopify; see schema.md). The adapter decides serialization —
       properties[...] inputs for Shopify, cart-item meta for WooCommerce. Uploads and
       the preview render are handled as blobs in prepareAndSubmit, not here. */
    collectProperties() {
      const list = [];
      const add = (key, value) => {
        if (value == null || value === '') return;
        list.push({ key, value });
      };

      this.optionGroups.forEach(g => {
        if (g.appliesTo && !g.appliesTo.includes(this.state.design)) return;
        add(g.property, this.state[g.id]);
      });

      const mode = this.markingModes.find(m => m.id === this.state.design);
      if (mode) {
        add(mode.property, mode.propertyValue);
        if (mode.inputs) {
          mode.inputs.forEach(inp => add(inp.property, this.state[mode.id + '_' + inp.id]));
          const font = mode.fonts && mode.fonts.find(f => f.value === this.state[mode.id + '_font']);
          if (font && font.property) add(font.property, font.value);
        }
        // House motif, per shoe: visible Right/Left Shoe = design name (like letters),
        // hidden _Right/_Left Shoe = motif art URL (like uploads).
        if (mode.motifs && this.state.emblemSource === 'motif') {
          (mode.uploads || []).forEach(u => {
            const item = mode.motifs.items.find(x => x.id === this.state['emblemMotif_' + u.id]);
            if (!item) return;
            const url = new URL(this.assetUrl(item.asset), location.href).href;
            add(u.property, url);                          // hidden _Right/_Left Shoe = art URL
            add(u.property.replace(/^_/, ''), item.name);  // visible Right/Left Shoe = design name
          });
        }
      }

      const fit = this.config.fit;
      add(fit.regions.property, this.state.region);
      add(fit.sizes.property.replace('{region}', this.state.region), this.state.size);
      if (fit.width) add(fit.width.property, this.state.width);

      const consentDef = this.stepDefs.find(s => s.id === 'review').consent;
      if (mode && mode.id === consentDef.appliesTo && this.state.emblemSource !== 'motif') {
        const consentBox = this.root.querySelector('[data-ma-consent-box]');
        if (consentBox && consentBox.checked) add('_Rights Confirmed', 'yes');
      }

      return list;
    }

    async prepareAndSubmit(button) {
      if (this.submitting) return;
      const consentRow = this.root.querySelector('[data-ma-consent-row]');
      const consentBox = this.root.querySelector('[data-ma-consent-box]');
      const v5 = this.root.querySelector('[data-ma-v5]');
      if (consentRow && !consentRow.hidden && consentBox && !consentBox.checked) {
        v5.textContent = 'Please confirm you have the rights to use this artwork.';
        return;
      }

      this.setSubmitting(true);
      try {
        const mode = this.markingModes.find(m => m.id === this.state.design);
        const motifMode = mode && mode.motifs && this.state.emblemSource === 'motif';

        // Collect the design as neutral data: the property list, the preview render,
        // and the per-shoe processed-artwork blobs. The adapter serializes it for its
        // store (Shopify form fields here; WP media upload + cart meta on WooCommerce).
        const properties = this.collectProperties();

        const previewBlob = await this.toBlobAsync(this.canvas);

        const processedItems = [];
        if (mode && mode.uploads && !motifMode) {
          for (const u of mode.uploads) {
            const uploaded = this.state[mode.id + '_' + u.id];
            const processedInput = this.emblemProcessedInputs[mode.id + '_' + u.id];
            if (uploaded && processedInput) {
              const blob = await this.toBlobAsync(uploaded.canvas);
              processedItems.push({ input: processedInput, blob, name: `${u.id}-processed.png` });
            }
          }
        }

        await this.adapter.submit({
          properties,
          motifMode,
          previewBlob,
          processedItems,
          uploadInputs: this.emblemUploadInputs,
          processedInputs: this.emblemProcessedInputs,
        });
      } catch (err) {
        this.setSubmitting(false);
        this.say('We could not prepare your order. Please try again.');
      }
    }

    // ---------- canvas render ----------
    render() {
      this.persist();
      if (this.renderQueued) return;
      this.renderQueued = true;
      requestAnimationFrame(async () => {
        this.renderQueued = false;
        await this.paint();
      });
    }

    async paint() {
      const W = this.canvas.width, H = this.canvas.height;
      this.cx.fillStyle = '#ffffff';
      this.cx.fillRect(0, 0, W, H);

      for (const layerDef of this.view.layers) {
        if (layerDef.type === 'optionImage') {
          const group = this.optionGroups.find(g => g.id === layerDef.optionGroup);
          const value = group.values.find(v => v.value === this.state[group.id]);
          const img = await this.layer(value && value.assets ? value.assets[this.view.id] : null);
          if (img) this.cx.drawImage(img, 0, 0, W, H);
        }
      }
      if (this.fontsReady) await this.fontsReady;

      for (const layerDef of this.view.layers) {
        if (layerDef.type === 'marking') this.paintMarking(layerDef.slot, W, H);
      }
    }

    paintMarking(slotId, W, H) {
      const slot = this.view.slots[slotId];
      const mode = this.markingModes.find(m => m.id === this.state.design);
      if (!mode) return;
      if (mode.inputs) {
        const inputDef = mode.inputs.find(inp => inp.slot === slotId);
        const text = this.state[mode.id + '_' + inputDef.id];
        if (!text) return;
        const font = mode.fonts.find(f => f.value === this.state[mode.id + '_font']) || mode.fonts[0];
        const threadGroup = this.optionGroups.find(g => g.appliesTo && g.appliesTo.includes(mode.id));
        const threadValue = threadGroup ? threadGroup.values.find(v => v.value === this.state[threadGroup.id]) : null;
        const threadHex = threadValue ? threadValue.hex : '#FFD700';
        this.drawText(text, slot, font.family, threadHex, W, H, inputDef.pattern === '[0-9]' ? '0' : 'H');
      } else if (mode.uploads || mode.motifs) {
        const uploadDef = mode.uploads.find(u => u.slot === slotId);
        if (mode.motifs && this.state.emblemSource === 'motif') {
          const motifId = uploadDef && this.state['emblemMotif_' + uploadDef.id];
          const render = motifId && this.motifRenderCache[motifId];
          if (render) this.drawEmblem(render, slot, W, H);
        } else if (uploadDef) {
          const uploaded = this.state[mode.id + '_' + uploadDef.id];
          if (uploaded) this.drawEmblem(uploaded.canvas, slot, W, H);
        }
      }
    }

    inkMeasure(txt, fontFamily) {
      const key = fontFamily + '|' + txt;
      if (this.inkCache[key]) return this.inkCache[key];
      const t = document.createElement('canvas'); t.width = 640; t.height = 256;
      const tc = t.getContext('2d');
      tc.font = `100px "${fontFamily}"`; tc.textAlign = 'center'; tc.textBaseline = 'middle';
      tc.fillStyle = '#fff'; tc.fillText(txt, 320, 128);
      const d = tc.getImageData(0, 0, 640, 256).data;
      let x0 = 640, y0 = 256, x1 = 0, y1 = 0, found = false;
      for (let y = 0; y < 256; y++) for (let x = 0; x < 640; x++) {
        if (d[(y * 640 + x) * 4 + 3] > 10) { found = true; if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
      }
      const r = found ? { w: x1 - x0 + 1, h: y1 - y0 + 1, cx: (x0 + x1) / 2 - 320, cy: (y0 + y1) / 2 - 128 } : null;
      this.inkCache[key] = r;
      return r;
    }

    hsla(hex, dl, a) {
      const r = parseInt(hex.slice(1, 3), 16) / 255, g = parseInt(hex.slice(3, 5), 16) / 255, b = parseInt(hex.slice(5, 7), 16) / 255;
      const max = Math.max(r, g, b), min = Math.min(r, g, b); let h = 0, s = 0; const l = (max + min) / 2;
      if (max !== min) {
        const d = max - min; s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        h = max === r ? ((g - b) / d + (g < b ? 6 : 0)) : max === g ? ((b - r) / d + 2) : ((r - g) / d + 4); h /= 6;
      }
      const nl = Math.max(0, Math.min(1, l + dl));
      return `hsla(${Math.round(h * 360)},${Math.round(s * 100)}%,${Math.round(nl * 100)}%,${a})`;
    }

    /* Satin-stitch treatment: glyphs render to an offscreen layer, then — clipped to
       the ink — angled thread striping, a sheen gradient, and a stitched edge; the
       layer composites onto the velvet with a soft shadow so the thread sits proud. */
    drawText(txt, slot, fontFamily, threadHex, W, H, refChar) {
      const targetH = slot.text.maxH * H, maxW = slot.text.boxW * W;
      const probe = this.inkMeasure(txt, fontFamily);
      if (!probe) return;
      // Scale against a FIXED reference glyph (cap height), never the typed string's own
      // ink box. Sizing off the string means "ab" - x-height only - is scaled up to the
      // height of "AB", and a descender in "Ag" shrinks the whole word. The reference
      // keeps every string at one size on one baseline, which is how it is stitched.
      // Caps-only text renders exactly as it did before.
      const ref = this.inkMeasure(refChar || 'H', fontFamily) || probe;
      let scale = targetH / ref.h;
      if (probe.w * scale > maxW) scale = maxW / probe.w;
      const size = 100 * scale;
      const x = slot.cx * W, yC = slot.cy * H;
      // Horizontal: centre the actual ink. Vertical: anchor the reference box, so the
      // baseline holds whatever mix of ascenders and descenders is typed.
      const dx = x - probe.cx * scale, dy = yC - ref.cy * scale;
      const oc = document.createElement('canvas'); oc.width = W; oc.height = H;
      const o = oc.getContext('2d');
      o.textAlign = 'center'; o.textBaseline = 'middle';
      o.font = `${size}px "${fontFamily}"`;
      const baselineY = dy;
      o.fillStyle = threadHex;
      o.fillText(txt, dx, baselineY);

      o.globalCompositeOperation = 'source-atop';
      const inkCY = dy + probe.cy * scale;
      const top = inkCY - (probe.h * scale) / 2 - 4, bot = inkCY + (probe.h * scale) / 2 + 4;
      const left = x - (probe.w * scale) / 2 - 6, right = x + (probe.w * scale) / 2 + 6;
      for (let i = 1; i <= 4; i++) {
        o.fillStyle = this.hsla(threadHex, -0.10, 0.03 + i * 0.03);
        o.fillText(txt, dx + i * 0.7, baselineY + i * 0.9);
        o.fillStyle = this.hsla(threadHex, 0.18, 0.07 + i * 0.03);
        o.fillText(txt, dx - i * 0.55, baselineY - i * 0.75);
      }
      const slant = (bot - top) * 0.18;
      o.lineWidth = 1.25;
      for (let sx = left - slant; sx < right; sx += 3) {
        for (const [ddx, col] of [[0, this.hsla(threadHex, 0.22, 0.7)], [1.5, this.hsla(threadHex, -0.18, 0.55)]]) {
          o.strokeStyle = col;
          o.beginPath();
          for (let y = top; y <= bot; y += 5) {
            const t = (y - top) / (bot - top);
            const px = sx + ddx + slant * t + Math.sin(y * 0.35 + sx * 1.7) * 0.7;
            y === top ? o.moveTo(px, y) : o.lineTo(px, y);
          }
          o.stroke();
        }
      }
      const g = o.createLinearGradient(left, top, right, bot);
      g.addColorStop(0, this.hsla(threadHex, 0.2, 0.6));
      g.addColorStop(0.5, this.hsla(threadHex, 0.2, 0));
      g.addColorStop(0.85, this.hsla(threadHex, -0.09, 0.18));
      g.addColorStop(1, this.hsla(threadHex, -0.09, 0.3));
      o.fillStyle = g; o.fillRect(left, top, right - left, bot - top);
      o.setLineDash([2.6, 1.6]);
      o.lineWidth = 1.8; o.strokeStyle = this.hsla(threadHex, -0.22, 0.55);
      o.strokeText(txt, dx, baselineY);
      o.lineWidth = 1; o.strokeStyle = this.hsla(threadHex, 0.22, 0.5);
      o.strokeText(txt, dx - 0.7, baselineY - 0.7);
      o.setLineDash([]);
      for (let i = 0; i < 140; i++) {
        const fx = left + Math.random() * (right - left), fy = top + Math.random() * (bot - top);
        o.fillStyle = `rgba(255,255,255,${0.03 + Math.random() * 0.07})`;
        o.fillRect(fx, fy, 1, 1);
      }
      o.globalCompositeOperation = 'source-over';

      this.cx.save();
      this.cx.shadowColor = 'rgba(0,0,0,0.45)'; this.cx.shadowBlur = 5; this.cx.shadowOffsetY = 2;
      this.cx.drawImage(oc, 0, 0);
      this.cx.restore();
    }

    drawEmblem(imgOrCanvas, slot, W, H) {
      const maxW = slot.emblem.boxW * W, maxH = slot.emblem.maxH * H;
      const s = Math.min(maxW / imgOrCanvas.width, maxH / imgOrCanvas.height);
      const w = imgOrCanvas.width * s, h = imgOrCanvas.height * s;
      this.cx.save();
      this.cx.shadowColor = 'rgba(0,0,0,0.45)'; this.cx.shadowBlur = 5; this.cx.shadowOffsetY = 2;
      this.cx.drawImage(imgOrCanvas, slot.cx * W - w / 2, slot.cy * H - h / 2, w, h);
      this.cx.restore();
    }
  }

  function boot() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(root => {
      if (root.dataset.maBooted) return;
      root.dataset.maBooted = 'true';
      new MaConfigurator(root).init();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
